import {
  type ActionRequirement,
  type ActionValidationResult,
  getAxeRequirements,
  getConstructionDefinition,
  getEquipmentSlotForItem,
  getEquipmentSlotName,
  getFishingRequirements,
  getItemName,
  getObjectFuelConfig,
  getPickaxeRequirements,
  getWorkstationFuelUnitsFromState,
  type ItemTypeValue,
  isEquippableItem,
  isFishingSpot,
  isFood,
  isWaterTile,
  ObjectCategory,
  ObjectType,
  type ObjectTypeValue,
  PawnCommandType,
  requiresAxe,
  requiresPickaxe,
  type TileTypeValue,
  toItemType,
  toObjectType,
} from '@dreamgame/shared';
import {
  sendCancelConstruction,
  sendOpenWorkstation,
  sendPawnCommand,
} from '../network/NetworkClient';
import { getTargetObjectProperties, getTargetObjectType, isObjectTarget } from './actionHelpers';
import { validateActionRequirements } from './actionValidation';
import { getTileAtWorld } from './chunks';
import {
  type ContextMenuAction,
  type ContextMenuTarget,
  closeContextMenu,
  isShiftQueued,
} from './contextMenu';
import { getWorkstationQueueCount, openWorkstation, openWorkstationPanel } from './crafting';
import { getPawn, playerId } from './entities';
import { objectName, t } from './i18n';
import { getSelectedIds, selectedPawns } from './selection';

// Validation result passed to action generators
interface GeneratorValidationResult {
  readonly canPerform: boolean;
  readonly disabledReasons: ReadonlyArray<string>;
  readonly validationResult: ActionValidationResult;
}

// Action generators based on object category and properties
interface ActionGenerator {
  readonly id: string;
  readonly matches: ({ target }: { target: ContextMenuTarget }) => boolean;
  readonly getRequirements?: ({
    target,
  }: {
    target: ContextMenuTarget;
  }) => ReadonlyArray<ActionRequirement>;
  readonly generate: ({
    target,
    validationResult,
  }: {
    target: ContextMenuTarget;
    validationResult: GeneratorValidationResult | undefined;
  }) => ContextMenuAction | ReadonlyArray<ContextMenuAction> | null;
  readonly priority: number;
}

// Registry of action generators
const actionGenerators: ActionGenerator[] = [];

// Register an action generator
export const registerActionGenerator = ({ generator }: { generator: ActionGenerator }): void => {
  actionGenerators.push(generator);
  // Sort by priority (higher = first)
  actionGenerators.sort((a, b) => b.priority - a.priority);
};

// Check if result is a single action
const isSingleAction = (
  result: ContextMenuAction | ReadonlyArray<ContextMenuAction>
): result is ContextMenuAction => {
  return 'id' in result && 'label' in result && 'execute' in result;
};

// Process a single generator and add results to actions array
const processGenerator = ({
  generator,
  target,
  actions,
}: {
  generator: ActionGenerator;
  target: ContextMenuTarget;
  actions: ContextMenuAction[];
}): void => {
  if (!generator.matches({ target })) {
    return;
  }

  const requirements = generator.getRequirements?.({ target }) ?? [];
  const validationResult =
    requirements.length > 0 ? validateActionRequirements({ requirements }) : undefined;

  const result = generator.generate({ target, validationResult });
  if (!result) {
    return;
  }

  if (isSingleAction(result)) {
    actions.push(result);
  } else {
    for (const action of result) {
      actions.push(action);
    }
  }
};

// Generate actions for a target
export const generateActions = ({
  target,
}: {
  target: ContextMenuTarget;
}): ReadonlyArray<ContextMenuAction> => {
  const actions: ContextMenuAction[] = [];

  for (const generator of actionGenerators) {
    processGenerator({ generator, target, actions });
  }

  return actions;
};

// Helper to get verb for harvesting based on object category
const getHarvestVerb = ({ objectType }: { objectType: ObjectTypeValue }): string => {
  // Trees
  if (
    objectType === ObjectType.TREE_OAK ||
    objectType === ObjectType.TREE_PINE ||
    objectType === ObjectType.TREE_BIRCH
  ) {
    return 'Chop';
  }

  // Rocks and boulders
  if (
    objectType === ObjectType.ROCK_SMALL ||
    objectType === ObjectType.ROCK_MEDIUM ||
    objectType === ObjectType.ROCK_LARGE ||
    objectType === ObjectType.BOULDER
  ) {
    return 'Mine';
  }

  // Ores
  if (
    objectType === ObjectType.ORE_IRON ||
    objectType === ObjectType.ORE_GOLD ||
    objectType === ObjectType.ORE_COPPER
  ) {
    return 'Mine';
  }

  // Bushes
  if (objectType === ObjectType.BUSH) {
    return 'Harvest';
  }

  return 'Gather';
};

// Helper to send gather command
const sendGatherCommand = ({ target }: { target: ContextMenuTarget }): void => {
  const selectedPawnIds = getSelectedIds();
  if (selectedPawnIds.length === 0 || !target.object) {
    return;
  }

  sendPawnCommand({
    pawnIds: selectedPawnIds,
    commandType: PawnCommandType.GATHER,
    targetX: target.tileX,
    targetY: target.tileY,
    targetEntity: target.object.networkId,
    queue: isShiftQueued.value,
  });
};

// === BUILT-IN ACTION GENERATORS ===

// Harvest/Gather - for harvestable objects
registerActionGenerator({
  generator: {
    id: 'harvest',
    priority: 100,
    matches: ({ target }) => {
      if (!isObjectTarget(target)) {
        return false;
      }
      const props = getTargetObjectProperties({ target });
      return props.harvestable;
    },
    getRequirements: ({ target }) => {
      if (!isObjectTarget(target)) {
        return [];
      }

      const objType = getTargetObjectType({ target });

      // Check if this object requires a pickaxe (rocks, ores)
      if (requiresPickaxe({ objectType: objType })) {
        const objName = objectName({ objectType: objType });
        return getPickaxeRequirements({ objectType: objType, objectName: objName });
      }

      // Check if this object requires an axe (trees)
      if (requiresAxe({ objectType: objType })) {
        return getAxeRequirements();
      }

      // Non-tool harvestables have no special requirements
      return [];
    },
    generate: ({ target, validationResult }) => {
      if (!isObjectTarget(target)) {
        return null;
      }

      const objType = getTargetObjectType({ target });
      const verb = getHarvestVerb({ objectType: objType });
      const objName = target.objectName ?? 'Unknown';

      // Determine if disabled and get reasons
      const disabled = validationResult ? !validationResult.canPerform : false;
      const disabledReasons =
        validationResult && validationResult.disabledReasons.length > 0
          ? validationResult.disabledReasons
          : undefined;

      return {
        id: 'harvest',
        label: `${verb} ${objName}`,
        color: disabled ? 'default' : 'success',
        disabled,
        disabledReasons,
        execute: () => sendGatherCommand({ target }),
      };
    },
  },
});

// Fish at fishing spot - for fishing spot world objects
registerActionGenerator({
  generator: {
    id: 'fish-at-spot',
    priority: 100,
    matches: ({ target }) => {
      if (!isObjectTarget(target)) {
        return false;
      }
      const objType = getTargetObjectType({ target });
      return isFishingSpot({ objectType: objType });
    },
    getRequirements: () => getFishingRequirements(),
    generate: ({ target, validationResult }) => {
      if (!isObjectTarget(target)) {
        return null;
      }

      const objName = target.objectName ?? 'Fishing Spot';
      const disabled = validationResult ? !validationResult.canPerform : false;
      const disabledReasons =
        validationResult && validationResult.disabledReasons.length > 0
          ? validationResult.disabledReasons
          : undefined;

      return {
        id: 'fish-at-spot',
        label: `Fish at ${objName}`,
        color: disabled ? 'default' : 'success',
        disabled,
        disabledReasons,
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0 || !isObjectTarget(target)) {
            return;
          }

          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.FISH,
            targetX: target.tileX,
            targetY: target.tileY,
            targetEntity: target.object.networkId,
            queue: isShiftQueued.value,
          });
        },
      };
    },
  },
});

// Fish at water edge - for water tiles without a fishing spot
registerActionGenerator({
  generator: {
    id: 'fish-at-water',
    priority: 80,
    matches: ({ target }) => {
      if (target.type !== 'ground') {
        return false;
      }
      const tileType = getTileAtWorld({ tileX: target.tileX, tileY: target.tileY });
      if (tileType === undefined) {
        return false;
      }
      return isWaterTile({ tileType: tileType as TileTypeValue });
    },
    getRequirements: () => getFishingRequirements(),
    generate: ({ target, validationResult }) => {
      const disabled = validationResult ? !validationResult.canPerform : false;
      const disabledReasons =
        validationResult && validationResult.disabledReasons.length > 0
          ? validationResult.disabledReasons
          : undefined;

      return {
        id: 'fish-at-water',
        label: 'Fish here',
        color: disabled ? 'default' : 'success',
        disabled,
        disabledReasons,
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0) {
            return;
          }

          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.FISH,
            targetX: target.tileX,
            targetY: target.tileY,
            targetEntity: 0,
            queue: isShiftQueued.value,
          });
        },
      };
    },
  },
});

// Use workstation - opens crafting panel
registerActionGenerator({
  generator: {
    id: 'use-workstation',
    priority: 90,
    matches: ({ target }) => {
      if (!isObjectTarget(target)) {
        return false;
      }
      const props = getTargetObjectProperties({ target });
      return props.category === ObjectCategory.WORKSTATION;
    },
    generate: ({ target }) => ({
      id: 'use-workstation',
      label: `Use ${target.objectName}`,
      color: 'success',
      execute: () => {
        if (!isObjectTarget(target)) {
          return;
        }

        const currentlyOpen = openWorkstation.value;
        if (currentlyOpen?.networkId === target.object.networkId) {
          closeContextMenu();
          return;
        }

        const objectType = getTargetObjectType({ target });

        // Open crafting panel locally
        openWorkstationPanel({
          networkId: target.object.networkId,
          objectType,
          tileX: target.tileX,
          tileY: target.tileY,
        });

        // Request workstation data from server
        sendOpenWorkstation({
          workstationNetworkId: target.object.networkId,
        });

        // Close context menu
        closeContextMenu();
      },
    }),
  },
});

// Work at workstation - assign pawns to work on crafting queue until empty
registerActionGenerator({
  generator: {
    id: 'work-at-workstation',
    priority: 85,
    matches: ({ target }) => {
      // Must have pawns selected
      if (getSelectedIds().length === 0) {
        return false;
      }
      if (!isObjectTarget(target)) {
        return false;
      }
      const props = getTargetObjectProperties({ target });
      if (props.category !== ObjectCategory.WORKSTATION) {
        return false;
      }
      // Only show if workstation has items in queue
      const queueCount = getWorkstationQueueCount({ networkId: target.object.networkId });
      return queueCount > 0;
    },
    generate: ({ target }) => ({
      id: 'work-at-workstation',
      label: `Work at ${target.objectName}`,
      color: 'success',
      execute: () => {
        const selectedPawnIds = getSelectedIds();
        if (selectedPawnIds.length === 0 || !isObjectTarget(target)) {
          return;
        }

        sendPawnCommand({
          pawnIds: selectedPawnIds,
          commandType: PawnCommandType.WORK_AT_WORKSTATION,
          targetX: target.tileX,
          targetY: target.tileY,
          targetEntity: target.object.networkId,
          queue: isShiftQueued.value,
        });

        closeContextMenu();
      },
    }),
  },
});

// Refuel fuel-powered workstation
registerActionGenerator({
  generator: {
    id: 'refuel-workstation',
    priority: 84,
    matches: ({ target }) => {
      if (getSelectedIds().length === 0) {
        return false;
      }
      if (!isObjectTarget(target)) {
        return false;
      }

      const objectType = getTargetObjectType({ target });
      const fuelConfig = getObjectFuelConfig({ objectType });
      if (!fuelConfig) {
        return false;
      }

      const currentFuel = getWorkstationFuelUnitsFromState({
        state: target.object.state ?? 0,
        maxFuelUnits: fuelConfig.maxFuelUnits,
      });
      return currentFuel < fuelConfig.maxFuelUnits;
    },
    generate: ({ target }) => ({
      id: 'refuel-workstation',
      label: t('ui.actions.refuelObject', { name: target.objectName ?? '' }),
      color: 'success',
      execute: () => {
        const selectedPawnIds = getSelectedIds();
        if (selectedPawnIds.length === 0 || !isObjectTarget(target)) {
          return;
        }

        sendPawnCommand({
          pawnIds: selectedPawnIds,
          commandType: PawnCommandType.REFUEL_WORKSTATION,
          targetX: target.tileX,
          targetY: target.tileY,
          targetEntity: target.object.networkId,
          queue: isShiftQueued.value,
        });

        closeContextMenu();
      },
    }),
  },
});

// Open/Close door
registerActionGenerator({
  generator: {
    id: 'door-interact',
    priority: 95,
    matches: ({ target }) => {
      if (!isObjectTarget(target)) {
        return false;
      }
      const objectType = getTargetObjectType({ target });
      return objectType === ObjectType.DOOR_WOOD || objectType === ObjectType.DOOR_METAL;
    },
    generate: ({ target }) => {
      const isOpen = target.object?.state === 1;
      return {
        id: 'door-interact',
        label: isOpen ? 'Close' : 'Open',
        execute: () => {
          console.log(`${isOpen ? 'Closing' : 'Opening'} door`);
          // TODO: Send door toggle command
        },
      };
    },
  },
});

// Sleep in bed
registerActionGenerator({
  generator: {
    id: 'sleep',
    priority: 90,
    matches: ({ target }) => {
      if (!isObjectTarget(target)) {
        return false;
      }
      const objectType = getTargetObjectType({ target });
      return objectType === ObjectType.BED || objectType === ObjectType.SLEEPING_PAD;
    },
    generate: ({ target }) => ({
      id: 'sleep',
      label: t('ui.labels.rest'),
      color: 'success',
      execute: () => {
        const selectedPawnIds = getSelectedIds();
        if (selectedPawnIds.length === 0 || !isObjectTarget(target)) {
          return;
        }

        sendPawnCommand({
          pawnIds: selectedPawnIds,
          commandType: PawnCommandType.SLEEP_IN_BED,
          targetX: target.tileX,
          targetY: target.tileY,
          targetEntity: target.object.networkId,
          queue: isShiftQueued.value,
        });

        closeContextMenu();
      },
    }),
  },
});

// Open storage
registerActionGenerator({
  generator: {
    id: 'open-storage',
    priority: 90,
    matches: ({ target }) => {
      if (!isObjectTarget(target)) {
        return false;
      }
      const objectType = getTargetObjectType({ target });
      return objectType === ObjectType.STORAGE_CRATE || objectType === ObjectType.STORAGE_SHELF;
    },
    generate: ({ target }) => ({
      id: 'open-storage',
      label: `Open ${target.objectName}`,
      execute: () => {
        console.log(`Opening storage: ${target.objectName}`);
        // TODO: Open storage UI
      },
    }),
  },
});

// Attack (for destructible objects not owned by player)
registerActionGenerator({
  generator: {
    id: 'attack-object',
    priority: 50,
    matches: ({ target }) => {
      if (!isObjectTarget(target)) {
        return false;
      }
      const props = getTargetObjectProperties({ target });
      // Only show attack for structures not harvestable
      return (
        props.destructible && !props.harvestable && props.category === ObjectCategory.STRUCTURE
      );
    },
    generate: ({ target }) => ({
      id: 'attack-object',
      label: `Attack ${target.objectName}`,
      color: 'danger',
      execute: () => {
        const selectedPawnIds = getSelectedIds();
        if (selectedPawnIds.length === 0 || !isObjectTarget(target)) {
          return;
        }

        sendPawnCommand({
          pawnIds: selectedPawnIds,
          commandType: PawnCommandType.ATTACK,
          targetX: target.tileX,
          targetY: target.tileY,
          targetEntity: target.object.networkId,
          queue: isShiftQueued.value,
        });
      },
    }),
  },
});

// Attack animal
registerActionGenerator({
  generator: {
    id: 'attack-animal',
    priority: 100,
    matches: ({ target }) => target.type === 'animal' && !!target.entityNetworkId,
    generate: ({ target }) => ({
      id: 'attack-animal',
      label: `Attack ${target.entityName ?? 'Animal'}`,
      color: 'danger',
      execute: () => {
        const selectedPawnIds = getSelectedIds();
        if (selectedPawnIds.length === 0 || !target.entityNetworkId) {
          return;
        }

        sendPawnCommand({
          pawnIds: selectedPawnIds,
          commandType: PawnCommandType.ATTACK,
          targetX: target.tileX,
          targetY: target.tileY,
          targetEntity: target.entityNetworkId,
          queue: isShiftQueued.value,
        });
      },
    }),
  },
});

// Attack enemy pawn
registerActionGenerator({
  generator: {
    id: 'attack-pawn',
    priority: 110,
    matches: ({ target }) => {
      if (target.type !== 'pawn' || !target.pawnData) {
        return false;
      }
      return target.pawnData.ownerId !== playerId.value && target.pawnData.isDowned === 0;
    },
    generate: ({ target }) => {
      if (!target.pawnData) {
        return null;
      }

      const pawnName = target.pawnData.name ?? 'Pawn';

      return {
        id: 'attack-pawn',
        label: `Attack ${pawnName}`,
        color: 'danger',
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0 || !target.pawnData) {
            return;
          }

          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.ATTACK,
            targetX: target.tileX,
            targetY: target.tileY,
            targetEntity: target.pawnData.networkId,
            queue: isShiftQueued.value,
          });
        },
      };
    },
  },
});

// Hunt animal (for carnivores/omnivores - just attack but with different label)
registerActionGenerator({
  generator: {
    id: 'hunt-animal',
    priority: 95,
    matches: ({ target }) => target.type === 'animal' && !!target.entityNetworkId,
    generate: ({ target }) => ({
      id: 'hunt-animal',
      label: `Hunt ${target.entityName ?? 'Animal'}`,
      color: 'warning',
      execute: () => {
        const selectedPawnIds = getSelectedIds();
        if (selectedPawnIds.length === 0 || !target.entityNetworkId) {
          return;
        }

        sendPawnCommand({
          pawnIds: selectedPawnIds,
          commandType: PawnCommandType.ATTACK,
          targetX: target.tileX,
          targetY: target.tileY,
          targetEntity: target.entityNetworkId,
          queue: isShiftQueued.value,
        });
      },
    }),
  },
});

// Construct - assign pawns to work on a construction site
registerActionGenerator({
  generator: {
    id: 'construct',
    priority: 100,
    matches: ({ target }) => target.type === 'construction' && !!target.constructionSite,
    generate: ({ target }) => {
      if (!target.constructionSite) {
        return null;
      }

      const objectType = toObjectType(target.constructionSite.objectType);
      const definition = getConstructionDefinition({ objectType });
      const name = definition?.name ?? 'Construction';

      return {
        id: 'construct',
        label: `Build ${name}`,
        color: 'success',
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0 || !target.constructionSite) {
            return;
          }

          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.BUILD,
            targetX: target.constructionSite.tileX,
            targetY: target.constructionSite.tileY,
            targetEntity: target.constructionSite.networkId,
            queue: isShiftQueued.value,
          });
        },
      };
    },
  },
});

// Cancel construction - remove a construction site
registerActionGenerator({
  generator: {
    id: 'cancel-construction',
    priority: 90,
    matches: ({ target }) => target.type === 'construction' && !!target.constructionSite,
    generate: ({ target }) => {
      if (!target.constructionSite) {
        return null;
      }

      return {
        id: 'cancel-construction',
        label: 'Cancel Construction',
        color: 'danger',
        execute: () => {
          if (!target.constructionSite) {
            return;
          }
          sendCancelConstruction({
            siteNetworkId: target.constructionSite.networkId,
          });
        },
      };
    },
  },
});

// Pick up ground items (supports multiple items at same location)
registerActionGenerator({
  generator: {
    id: 'pickup-ground-items',
    priority: 100,
    matches: ({ target }) =>
      target.type === 'groundItem' && !!target.groundItems && target.groundItems.length > 0,
    generate: ({ target }) => {
      if (!target.groundItems || target.groundItems.length === 0) {
        return null;
      }

      const actions: ContextMenuAction[] = [];

      // Generate individual pickup action for each item
      for (const groundItem of target.groundItems) {
        const itemType = toItemType(groundItem.itemType);
        const itemName = getItemName({ itemType });
        const quantity = groundItem.quantity;
        const label = quantity > 1 ? `Pick up ${itemName} (${quantity})` : `Pick up ${itemName}`;

        actions.push({
          id: `pickup-ground-item-${groundItem.networkId}`,
          label,
          color: 'success',
          execute: () => {
            const selectedPawnIds = getSelectedIds();
            if (selectedPawnIds.length === 0) {
              return;
            }

            sendPawnCommand({
              pawnIds: selectedPawnIds,
              commandType: PawnCommandType.PICKUP_GROUND_ITEM,
              targetX: groundItem.tileX,
              targetY: groundItem.tileY,
              targetEntity: groundItem.networkId,
              queue: isShiftQueued.value,
            });
          },
        });
      }

      // Add "Pick up all" option if there are multiple items
      if (target.groundItems.length > 1) {
        const allItems = target.groundItems;
        actions.push({
          id: 'pickup-all-ground-items',
          label: 'Pick up all',
          color: 'default',
          execute: () => {
            const selectedPawnIds = getSelectedIds();
            if (selectedPawnIds.length === 0) {
              return;
            }

            // Send pickup command for each item
            for (const groundItem of allItems) {
              sendPawnCommand({
                pawnIds: selectedPawnIds,
                commandType: PawnCommandType.PICKUP_GROUND_ITEM,
                targetX: groundItem.tileX,
                targetY: groundItem.tileY,
                targetEntity: groundItem.networkId,
                queue: isShiftQueued.value,
              });
            }
          },
        });
      }

      return actions;
    },
  },
});

// Pick up and eat food from ground (generic for all edibles)
registerActionGenerator({
  generator: {
    id: 'pickup-and-eat-ground-food',
    priority: 105, // Higher than generic pickup (100)
    matches: ({ target }) => {
      if (target.type !== 'groundItem' || !target.groundItems) {
        return false;
      }
      // Only match if at least one item is food
      return target.groundItems.some((item) => isFood(item.itemType));
    },
    generate: ({ target }) => {
      if (!target.groundItems || target.groundItems.length === 0) {
        return null;
      }

      const actions: ContextMenuAction[] = [];

      // Generate action for each food item
      for (const groundItem of target.groundItems) {
        if (!isFood(groundItem.itemType)) {
          continue;
        }

        const itemType = toItemType(groundItem.itemType);
        const itemName = getItemName({ itemType });
        const quantity = groundItem.quantity;
        const label = quantity > 1 ? `Eat ${itemName} (${quantity})` : `Eat ${itemName}`;

        actions.push({
          id: `eat-ground-food-${groundItem.networkId}`,
          label,
          color: 'success',
          execute: () => {
            const selectedPawnIds = getSelectedIds();
            if (selectedPawnIds.length === 0) {
              return;
            }

            sendPawnCommand({
              pawnIds: selectedPawnIds,
              commandType: PawnCommandType.PICKUP_AND_EAT,
              targetX: groundItem.tileX,
              targetY: groundItem.tileY,
              targetEntity: groundItem.networkId,
              queue: isShiftQueued.value,
            });
          },
        });
      }

      return actions.length > 0 ? actions : null;
    },
  },
});

// Eat food from inventory
registerActionGenerator({
  generator: {
    id: 'eat-food',
    priority: 100,
    matches: ({ target }) => {
      if (target.type !== 'inventoryItem' || !target.inventoryItem) {
        return false;
      }
      return isFood(target.inventoryItem.itemId);
    },
    generate: ({ target }) => {
      if (!target.inventoryItem) {
        return null;
      }

      return {
        id: 'eat-food',
        label: `Eat ${target.inventoryItem.itemName}`,
        color: 'success',
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0 || !target.inventoryItem) {
            return;
          }

          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.EAT,
            targetX: 0,
            targetY: 0,
            targetEntity: target.inventoryItem.itemId,
            queue: isShiftQueued.value,
          });
        },
      };
    },
  },
});

// Equip weapon/armor from inventory
registerActionGenerator({
  generator: {
    id: 'equip-item',
    priority: 95,
    matches: ({ target }) => {
      if (target.type !== 'inventoryItem' || !target.inventoryItem) {
        return false;
      }
      return isEquippableItem({ itemType: target.inventoryItem.itemId as ItemTypeValue });
    },
    generate: ({ target }) => {
      if (!target.inventoryItem) {
        return null;
      }

      const slot = getEquipmentSlotForItem({
        itemType: target.inventoryItem.itemId as ItemTypeValue,
      });
      const slotName = getEquipmentSlotName({ slot });

      return {
        id: 'equip-item',
        label: `Equip (${slotName})`,
        color: 'success',
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0 || !target.inventoryItem) {
            return;
          }

          // Send equip command - use slotIndex to identify which inventory item
          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.EQUIP_ITEM,
            targetX: 0,
            targetY: 0,
            targetEntity: target.inventoryItem.slotIndex,
          });
        },
      };
    },
  },
});

// Pick up corpse
registerActionGenerator({
  generator: {
    id: 'pickup-corpse',
    priority: 100,
    matches: ({ target }) => target.type === 'corpse' && !!target.corpse,
    generate: ({ target }) => {
      if (!target.corpse) {
        return null;
      }

      const corpseName = target.corpseName ?? 'Corpse';

      return {
        id: 'pickup-corpse',
        label: `Pick up ${corpseName}`,
        color: 'success',
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0 || !target.corpse) {
            return;
          }

          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.HAUL_CORPSE,
            targetX: Math.floor(target.corpse.x),
            targetY: Math.floor(target.corpse.y),
            targetEntity: target.corpse.networkId,
            queue: isShiftQueued.value,
          });
        },
      };
    },
  },
});

// Pickup downed pawn - just pick up without auto-bed routing
registerActionGenerator({
  generator: {
    id: 'pickup-downed-pawn',
    priority: 110,
    matches: ({ target }) => {
      if (target.type !== 'pawn' || !target.pawnData) {
        return false;
      }
      // Must be downed and not already being carried
      return target.pawnData.isDowned === 1 && target.pawnData.carriedByNetworkId === 0;
    },
    generate: ({ target }) => {
      if (!target.pawnData) {
        return null;
      }

      const pawnName = target.pawnData.name ?? 'Colonist';

      return {
        id: 'pickup-downed-pawn',
        label: `Pickup ${pawnName}`,
        color: 'warning',
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0 || !target.pawnData) {
            return;
          }

          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.PICKUP_DOWNED_PAWN,
            targetX: target.tileX,
            targetY: target.tileY,
            targetEntity: target.pawnData.networkId,
            queue: isShiftQueued.value,
          });
        },
      };
    },
  },
});

// Rescue downed pawn - auto takes to nearest bed or creates sleeping pad
registerActionGenerator({
  generator: {
    id: 'rescue-downed-pawn',
    priority: 105,
    matches: ({ target }) => {
      if (target.type !== 'pawn' || !target.pawnData) {
        return false;
      }
      // Must be downed and not already being carried
      return target.pawnData.isDowned === 1 && target.pawnData.carriedByNetworkId === 0;
    },
    generate: ({ target }) => {
      if (!target.pawnData) {
        return null;
      }

      const pawnName = target.pawnData.name ?? 'Colonist';

      return {
        id: 'rescue-downed-pawn',
        label: `Rescue ${pawnName}`,
        color: 'success',
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0 || !target.pawnData) {
            return;
          }

          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.RESCUE,
            targetX: target.tileX,
            targetY: target.tileY,
            targetEntity: target.pawnData.networkId,
            queue: isShiftQueued.value,
          });
        },
      };
    },
  },
});

// Place carried pawn in bed - when right-clicking a bed while carrying someone
registerActionGenerator({
  generator: {
    id: 'place-in-bed',
    priority: 120,
    matches: ({ target }) => {
      if (!isObjectTarget(target)) {
        return false;
      }

      // Must be a bed or sleeping pad
      const objectType = getTargetObjectType({ target });
      if (objectType !== ObjectType.BED && objectType !== ObjectType.SLEEPING_PAD) {
        return false;
      }

      // Check if any selected pawn is carrying someone
      const pawns = selectedPawns.value;
      return pawns.some((pawn) => (pawn.carryingNetworkId ?? 0) > 0);
    },
    generate: ({ target }) => {
      if (!isObjectTarget(target)) {
        return null;
      }

      const objectName = target.objectName ?? 'Bed';

      // Find the carried pawn's name
      let carriedName = 'colonist';
      const pawns = selectedPawns.value;
      for (const pawn of pawns) {
        if ((pawn.carryingNetworkId ?? 0) > 0) {
          const carriedPawn = getPawn({ networkId: pawn.carryingNetworkId });
          if (carriedPawn) {
            carriedName = carriedPawn.name ?? 'colonist';
          }
          break;
        }
      }

      return {
        id: 'place-in-bed',
        label: `Place ${carriedName} in ${objectName}`,
        color: 'success',
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0 || !isObjectTarget(target)) {
            return;
          }

          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.PLACE_IN_BED,
            targetX: target.tileX,
            targetY: target.tileY,
            targetEntity: target.object.networkId,
            queue: isShiftQueued.value,
          });
        },
      };
    },
  },
});

// Put down carried pawn - when right-clicking ground while carrying someone
registerActionGenerator({
  generator: {
    id: 'drop-carried-pawn',
    priority: 115,
    matches: ({ target }) => {
      // Works on ground targets
      if (target.type !== 'ground') {
        return false;
      }

      // Check if any selected pawn is carrying someone
      const pawns = selectedPawns.value;
      return pawns.some((pawn) => (pawn.carryingNetworkId ?? 0) > 0);
    },
    generate: ({ target }) => {
      // Find the carried pawn's name
      let carriedName = 'colonist';
      const pawns = selectedPawns.value;
      for (const pawn of pawns) {
        if ((pawn.carryingNetworkId ?? 0) > 0) {
          const carriedPawn = getPawn({ networkId: pawn.carryingNetworkId });
          if (carriedPawn) {
            carriedName = carriedPawn.name ?? 'colonist';
          }
          break;
        }
      }

      return {
        id: 'drop-carried-pawn',
        label: `Put down ${carriedName} here`,
        color: 'warning',
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0) {
            return;
          }

          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.DROP_CARRIED_PAWN,
            targetX: target.tileX,
            targetY: target.tileY,
            targetEntity: 0,
            queue: isShiftQueued.value,
          });
        },
      };
    },
  },
});

// Walk to location while carrying - allows movement without putting down
registerActionGenerator({
  generator: {
    id: 'walk-to-while-carrying',
    priority: 100,
    matches: ({ target }) => {
      // Works on ground targets
      if (target.type !== 'ground') {
        return false;
      }

      // Check if any selected pawn is carrying someone
      const pawns = selectedPawns.value;
      return pawns.some((pawn) => (pawn.carryingNetworkId ?? 0) > 0);
    },
    generate: ({ target }) => {
      return {
        id: 'walk-to-while-carrying',
        label: 'Walk to',
        execute: () => {
          const selectedPawnIds = getSelectedIds();
          if (selectedPawnIds.length === 0) {
            return;
          }

          sendPawnCommand({
            pawnIds: selectedPawnIds,
            commandType: PawnCommandType.MOVE_TO,
            targetX: target.tileX,
            targetY: target.tileY,
            targetEntity: 0,
            queue: isShiftQueued.value,
          });
        },
      };
    },
  },
});

// Cancel action
registerActionGenerator({
  generator: {
    id: 'cancel',
    priority: -100,
    matches: () => true,
    generate: () => ({
      id: 'cancel',
      label: 'Cancel',
      execute: () => closeContextMenu(),
    }),
  },
});
